/**
 * Message Utilities
 * Functions for formatting, sanitizing, and filtering chat messages
 */

import { createToolCallRegex, createToolButtonRegex } from "./toolParsing";
import i18n from "i18next";

const TOOL_INVOCATION_PATTERNS = [
        /\bprint\s*\(\s*farm[_-]kpi/i,
        /\bfarm[_-]kpi[\w-]*\s*\(/i,
        /\bexecute[_-]?chat[_-]?tool\b/i,
];

/**
 * Remove raw tool invocation snippets from AI output
 * @param {string} text - AI response text
 * @returns {string} Cleaned text without inline invocation snippets
 */
export const stripToolInvocationSyntax = (text) => {
        if (!text) {
                return "";
        }

        const cleanedLines = text
                .split("\n")
                .filter((line) => {
                        const trimmed = line.trim();

                        if (!trimmed) {
                                return true;
                        }

                        if (/^```/.test(trimmed)) {
                                return false;
                        }

                        return !TOOL_INVOCATION_PATTERNS.some((pattern) => pattern.test(trimmed));
                });

        return cleanedLines
                .join("\n")
                .replace(/\n{3,}/g, "\n\n")
                .trim();
};

/**
 * Strip tool call markers from text
 * @param {string} text - Text containing tool call markers
 * @returns {string} Text with tool call markers removed
 */
export const stripToolCallMarkers = (text) => {
        return (text || "").replace(createToolCallRegex(), "");
};

/**
 * Sanitize AI response by removing tool call markers and trimming whitespace
 * @param {string} text - AI response text
 * @returns {string} Sanitized text
 */
export const sanitizeAiResponse = (text) => {
        return stripToolCallMarkers(text).trim();
};

/**
 * Check if text has displayable content (text beyond tool buttons)
 * @param {string} text - Text to check
 * @returns {boolean} True if text has displayable content
 */
export const hasDisplayableContent = (text) => {
        if (!text) {
                return false;
        }

        const contentWithoutButtons = text.replace(createToolButtonRegex(), "").trim();

        if (contentWithoutButtons.length > 0) {
                return true;
        }

        return createToolButtonRegex().test(text);
};

/**
 * Determine if a message should be displayed to the user
 * Hides background processing (tool results, intermediate AI responses)
 * @param {Object} msg - Message object
 * @param {Array} allMessages - All messages in the conversation
 * @returns {boolean} True if message should be displayed
 */
export const shouldDisplayMessage = (msg, allMessages) => {
        // Always show user messages
        if (msg.sender === "user") return true;

        // Hide raw error codes (e.g., "42697") that leak from backend
        if (msg.sender === "ai" && msg.content && /^\d+$/.test(msg.content.trim())) {
                return false;
        }

        // Always show error messages
        if (msg.metadata?.type === "tool-error") return true;

        // Hide tool results
        if (msg.metadata?.type === "tool-result") return false;

        // Hide initial AI responses with tool calls (background processing)
        if (msg.metadata?.type === "ai-response" && msg.metadata?.toolCalls?.length > 0) {
                return false;
        }

        // Show final interpretation (marked with isFinal) or the last one if none marked
        if (msg.metadata?.type === "ai-interpretation") {
                // If marked as final, show it
                if (msg.metadata?.isFinal === true) {
                        return true;
                }
                // If this is the last ai-interpretation in the conversation, show it
                const interpretationMessages = allMessages.filter(m => 
                        m.metadata?.type === "ai-interpretation"
                );
                if (interpretationMessages.length > 0) {
                        const lastInterpretation = interpretationMessages[interpretationMessages.length - 1];
                        return msg.id === lastInterpretation.id;
                }
                return false;
        }

        // Show other AI messages (ai-response without tool calls, welcome messages, etc.)
        return true;
};

/**
 * Format timestamp to time string
 * @param {Object|Date|number} timestamp - Firestore timestamp or Date
 * @returns {string} Formatted time string
 */
export const formatTime = (timestamp) => {
        if (!timestamp) return "";
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const locale = i18n.language || navigator.language || "de";
        return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
};

/**
 * Format timestamp to date and time string
 * @param {Object|Date|number} timestamp - Firestore timestamp or Date
 * @returns {string} Formatted date and time string
 */
export const formatHistoryDateTime = (timestamp) => {
        if (!timestamp) {
                return "";
        }

        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

        if (Number.isNaN(date.getTime())) {
                return "";
        }

        const locale = i18n.language || navigator.language || "de";
        return date.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
};

